import { observer } from 'mobx-react';
import { useLocation, useParams, ParamParseKey, generatePath } from 'react-router-dom';

import { Button, Flex, Page } from '@servicetitan/anvil2';
import { useDependencies } from '@servicetitan/react-ioc';

import { useManageTenantGeneratePath } from '../../utils/use-manage-tenant-generate-path';
import {
    ManageTenantRoutes,
    AccountManagementRoutes,
    ConfigurationRoutes,
} from '../../utils/routes';
import { ManageTenantStore } from '../../stores/manage-tenant.store';
import { ManagePartitionStore } from '../../stores/manage-partition.store';
import { TenantSmsConfigurationStore } from '../../stores/tenant-sms-configuration.store';
import { LoadStatus } from '../../../common/enums/load-status';

const useIsManageTenantConfigurationRoute = () => {
    const location = useLocation();
    const { generateManageTenantPath } = useManageTenantGeneratePath();

    try {
        const configurationPath = generateManageTenantPath(ManageTenantRoutes.configuration);
        const emailDirectMailPath = `${configurationPath}/${ConfigurationRoutes.emailDirectMail}`;

        // Check if we're on the configuration route or the email-direct-mail sub-route
        return location.pathname === configurationPath || location.pathname === emailDirectMailPath;
    } catch {
        // If we can't generate the path (e.g., missing params), we're not on the route
        return false;
    }
};

const useIsSmsConfigurationRoute = () => {
    const location = useLocation();
    const { generateManageTenantPath } = useManageTenantGeneratePath();

    try {
        const configurationPath = generateManageTenantPath(ManageTenantRoutes.configuration);
        const smsPath = `${configurationPath}/${ConfigurationRoutes.sms}`;

        return location.pathname === smsPath;
    } catch {
        return false;
    }
};

const useIsManagePartitionConfigurationRoute = () => {
    const location = useLocation();
    const params = useParams<ParamParseKey<typeof AccountManagementRoutes.managePartition>>();

    try {
        if (!params.externalPartnerId || !params.externalTenantId || !params.externalPartitionId) {
            return false;
        }

        const partitionPath = generatePath(AccountManagementRoutes.managePartition, {
            externalPartnerId: params.externalPartnerId,
            externalTenantId: params.externalTenantId,
            externalPartitionId: params.externalPartitionId,
        });

        return location.pathname === `/accounts-management/${partitionPath}`;
    } catch {
        return false;
    }
};

export const Footer = () => {
    const isManageTenantConfigurationRoute = useIsManageTenantConfigurationRoute();
    const isSmsConfigurationRoute = useIsSmsConfigurationRoute();
    const isManagePartitionConfigurationRoute = useIsManagePartitionConfigurationRoute();

    if (
        !isManageTenantConfigurationRoute &&
        !isSmsConfigurationRoute &&
        !isManagePartitionConfigurationRoute
    ) {
        return null;
    }

    return (
        <Page.Footer justifyContent="flex-end">
            {isManageTenantConfigurationRoute && <TenantConfigurationFooter />}
            {isSmsConfigurationRoute && <SmsConfigurationFooter />}
            {isManagePartitionConfigurationRoute && <PartitionConfigurationFooter />}
        </Page.Footer>
    );
};

const SmsConfigurationFooter = observer(() => {
    const [{ save, saveLoadStatus, formState, isFormChanged, resetConfiguration }] =
        useDependencies(TenantSmsConfigurationStore);

    return (
        <Flex gap={4}>
            <Button onClick={resetConfiguration} disabled={!isFormChanged}>
                Cancel
            </Button>
            <Button
                appearance="primary"
                onClick={save}
                loading={saveLoadStatus === LoadStatus.Loading}
                disabled={
                    formState.hasError || saveLoadStatus === LoadStatus.Loading || !isFormChanged
                }
            >
                Save
            </Button>
        </Flex>
    );
});

const TenantConfigurationFooter = observer(() => {
    const [{ saveConfigs, saveLoadStatus, formState, isFormChanged, resetConfiguration }] =
        useDependencies(ManageTenantStore);

    return (
        <Flex gap={4}>
            <Button onClick={resetConfiguration} disabled={!isFormChanged}>
                Cancel
            </Button>
            <Button
                appearance="primary"
                onClick={saveConfigs}
                loading={saveLoadStatus === LoadStatus.Loading}
                disabled={
                    formState.hasError || saveLoadStatus === LoadStatus.Loading || !isFormChanged
                }
            >
                Save
            </Button>
        </Flex>
    );
});

const PartitionConfigurationFooter = observer(() => {
    const [{ saveConfigs, saveLoadStatus, formState, isFormChanged, resetConfiguration }] =
        useDependencies(ManagePartitionStore);

    return (
        <Flex gap={4}>
            <Button onClick={resetConfiguration} disabled={!isFormChanged}>
                Cancel
            </Button>
            <Button
                appearance="primary"
                onClick={saveConfigs}
                loading={saveLoadStatus === LoadStatus.Loading}
                disabled={
                    formState.hasError || saveLoadStatus === LoadStatus.Loading || !isFormChanged
                }
            >
                Save
            </Button>
        </Flex>
    );
});
